/**
 * An Area's icon (slice 6d spec §3.1): one of the chat icons (spike S-324),
 * Phosphor Duotone on a sage tile in the Area page's header, plain in the
 * sidebar; and the picker that chooses one.
 */
import { CHAT_ICON_COMPONENTS, ChatIcon } from "@crew/ui/components/chat-icon";
import type { IconComponent } from "@crew/ui/components/nav-icons";
import { cn } from "@crew/ui/lib/utils";

/** The sidebar row's icon: the Area's, at the nav icons' 16 px. */
export function areaNavIcon(name: string): IconComponent {
  function AreaNavIcon() {
    return <ChatIcon name={name} size={16} />;
  }
  return AreaNavIcon;
}

export function AreaTile({ icon, size = 44 }: { icon: string; size?: number }) {
  return (
    <span
      aria-hidden
      className="flex shrink-0 items-center justify-center rounded-[10px] bg-tile-sage text-foreground"
      style={{ width: size, height: size }}
    >
      <ChatIcon name={icon} size={Math.round(size * 0.55)} />
    </span>
  );
}

const ICONS = Object.keys(CHAT_ICON_COMPONENTS);

/** Every chat icon as a grid of buttons; `value` is ringed. */
export function IconPicker({
  value,
  onChange,
  disabled,
}: {
  value: string | null;
  onChange: (icon: string) => void;
  disabled?: boolean;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Icon"
      data-icon-picker
      className="grid max-h-[176px] grid-cols-8 gap-1 overflow-y-auto rounded-lg border border-border p-1.5 sm:grid-cols-10"
    >
      {ICONS.map((name) => (
        <button
          key={name}
          type="button"
          role="radio"
          aria-checked={value === name}
          aria-label={name.replace(/-/g, " ")}
          data-icon={name}
          disabled={disabled}
          onClick={() => onChange(name)}
          className={cn(
            "flex aspect-square items-center justify-center rounded-md text-foreground hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring",
            value === name && "bg-tile-sage ring-1 ring-foreground/40",
          )}
        >
          <ChatIcon name={name} size={18} />
        </button>
      ))}
    </div>
  );
}

/**
 * A row's Area (spec §4.4): its icon and label, small, after the title;
 * greyed once the Area is archived. Nothing for a row with no Area.
 */
export function AreaChip({
  area,
  className,
}: {
  area: { label: string; icon: string; archived?: boolean } | null | undefined;
  className?: string;
}) {
  if (!area) return null;
  return (
    <span
      data-area-chip={area.label}
      className={cn(
        "inline-flex h-5 max-w-[160px] shrink-0 items-center gap-1 rounded-tag bg-secondary px-1.5 align-middle text-xs font-medium",
        area.archived ? "text-hint" : "text-copy",
        className,
      )}
    >
      <ChatIcon name={area.icon} size={12} />
      <span className="truncate">{area.label}</span>
    </span>
  );
}
